import type { Vertex } from './AdjacencyList';
import {
  AdjacencyList,
  isDAG,
  topologicalSortWithDependencies,
  topologicalSortWithoutDependencies
} from './AdjacencyList';
import { ESearchDirection } from './AdjacencyList/AdjacencyList';
import type { Type } from './Type';
import { requireEqualPrototypes } from './Utils';

export class GraphPropsMapDiscrepancyError extends Error {
  public constructor(vertexDescriptor: VertexDescriptor) {
    super(
      'Graph vertex property map discrepancy detected.' +
        '\n  Vertex descriptor [' +
        vertexDescriptor +
        '] is expected to be existent.' +
        '\n  But it is not registered in property map.'
    );
    Object.setPrototypeOf(this, GraphPropsMapDiscrepancyError.prototype);
  }
}

export type TypeCollection = Array<Type>;

// --------------- IValue
export interface IValue {
  readonly type: Type;
  dispose?: () => void;
}

// --------------- TypedValue
export class TypedValue {
  public type: Type;

  public value: IValue | undefined;

  public constructor(type: Type, value?: IValue) {
    this.type = type;
    this.value = value;
  }

  public static FromValue(value: IValue): TypedValue {
    return new TypedValue(value.type, value);
  }
}
export type TypedValueCollection = Array<TypedValue>;

// --------------- IValueCalculator
export interface IValueCalculator {
  name?: string;
  inpProto: TypeCollection;
  resProto: Type;
  invokeAsync: (inputs: TypedValueCollection) => Promise<TypedValue | undefined>;
}

// --------------- Graph
// Vertex of graph is an abstract function, edge is dependency between functions
export type VertexDescriptor = Vertex;
export type VertexDescriptorCollection = Array<VertexDescriptor>;
export interface VertexDependencies {
  vertex: VertexDescriptor;
  dependencies?: VertexDescriptorCollection;
}
export type VerticesDependenciesCollection = Array<VertexDependencies>;

// --------------- _VertexProperty
interface _VertexProperty {
  calculator: IValueCalculator;
  value?: TypedValue;
}
type _VertexPropertyMap = Map<VertexDescriptor, _VertexProperty>;

//type _Memento = { adj: AdjacencyList, propsMap: _VertexPropertyMap };

// --------------- Graph
export class Graph {
  private _adj: AdjacencyList;

  private _propsMap: _VertexPropertyMap;

  public constructor() {
    this._adj = new AdjacencyList();
    this._propsMap = new Map();
  }

  public isDAG(): boolean {
    return isDAG(this._adj);
  }

  public topologicalSort(startVertices?: VertexDescriptorCollection): VerticesDependenciesCollection {
    return topologicalSortWithDependencies(
      ESearchDirection.Outputs,
      this._adj,
      startVertices || this.getInputs().reverse()
    );
  }

  public topologicalSortInv(startVertices?: VertexDescriptorCollection): VerticesDependenciesCollection {
    return topologicalSortWithDependencies(
      ESearchDirection.Inputs,
      this._adj,
      startVertices || this.getOutputs().reverse()
    );
  }

  public simpleTopologicalSort(startVertices?: VertexDescriptorCollection): VertexDescriptorCollection {
    return topologicalSortWithoutDependencies(
      ESearchDirection.Outputs,
      this._adj,
      startVertices || this.getInputs().reverse()
    );
  }

  public simpleTopologicalSortInv(startVertices?: VertexDescriptorCollection): VertexDescriptorCollection {
    return topologicalSortWithoutDependencies(
      ESearchDirection.Inputs,
      this._adj,
      startVertices || this.getOutputs().reverse()
    );
  }

  public ancestryInfo(startVertices: VertexDescriptorCollection): VerticesDependenciesCollection {
    const vertices = new Array<VertexDescriptor>();
    const dependencies = new Map<VertexDescriptor, Array<VertexDescriptor>>();

    this._adj.breadthFirstSearch(
      ESearchDirection.Inputs,
      {
        discoverVertex: (v: VertexDescriptor): void => {
          vertices.push(v);
        },
        examineEdge: (vFrom: VertexDescriptor, vTo: VertexDescriptor): void => {
          const dep = dependencies.get(vFrom);
          if (!dep) {
            dependencies.set(vFrom, [vTo]);
          } else {
            dep.push(vTo);
          }
        }
      },
      startVertices
    );

    return vertices.map((v) => ({ vertex: v, dependencies: dependencies.get(v) }));
  }

  private _addVertex(
    calculator: IValueCalculator,
    inputsOrder: VertexDescriptorCollection,
    initVertex?: VertexDescriptor
  ): VertexDescriptor {
    const expectedProto = calculator.inpProto;
    const providedProto = inputsOrder.map((u: VertexDescriptor): Type => {
      const uProp = this._propsMap.get(u);
      if (uProp === undefined) {
        throw new GraphPropsMapDiscrepancyError(u);
      }
      return uProp.calculator.resProto;
    });
    requireEqualPrototypes(expectedProto, providedProto);

    const v = initVertex !== undefined ? this._adj.addVertexRaw(initVertex) : this._adj.addVertex();
    for (const u of inputsOrder) {
      this._adj.addEdge(u, v);
    }

    this._propsMap.set(v, { calculator: calculator });

    return v;
  }

  public addVertex(calculator: IValueCalculator, inputsOrder: VertexDescriptorCollection): VertexDescriptor {
    return this._addVertex(calculator, inputsOrder);
  }

  public initVertex(
    calculator: IValueCalculator,
    inputsOrder: VertexDescriptorCollection,
    v: VertexDescriptor
  ): VertexDescriptor {
    return this._addVertex(calculator, inputsOrder, v);
  }

  public deleteVertex(v: VertexDescriptor): void {
    if (this._adj.getOutputs(v).length > 0) {
      throw new EvalError('Only leaf vertices could be deleted. Delete dependent vertices first.');
    }

    for (const u of this._adj.getInputs(v).slice()) {
      // slice here as deleteEdge mutates inputs
      this._adj.deleteEdge(u, v);
    }

    this._adj.deleteVertex(v);

    this._propsMap.delete(v);
  }

  private _getExternalDependencies(verticesToDrop: VertexDescriptorCollection): VertexDescriptorCollection {
    const allDependencies: Set<VertexDescriptor> = new Set();
    for (const v of verticesToDrop) {
      allDependencies.add(v);
      for (const u of this._adj.getOutputs(v)) {
        allDependencies.add(u);
      }
    }

    if (allDependencies.size == verticesToDrop.length) {
      return [];
    } else {
      for (const v of verticesToDrop) {
        allDependencies.delete(v);
      }
      return Array.from(allDependencies);
    }
  }

  public deleteDependentVertices(orderedVerticesToDrop: VertexDescriptorCollection): void {
    const externalDependencies = this._getExternalDependencies(orderedVerticesToDrop);
    if (externalDependencies.length > 0) {
      throw new EvalError(
        'Only self-dependent list of vertices could be deleted. Delete dependent external vertices first: ' +
          externalDependencies.toString()
      );
    }

    const reverseOrderedVertices = orderedVerticesToDrop.slice().reverse();
    for (const v of reverseOrderedVertices) {
      this.deleteVertex(v);
    }
  }

  public eraseVertex(v: VertexDescriptor): void {
    const vProp = this._propsMap.get(v);
    if (vProp === undefined) {
      throw new GraphPropsMapDiscrepancyError(v);
    }
    vProp.value?.value?.dispose?.();
    vProp.value = undefined;
  }

  public async calculateVertexAsync(v: VertexDescriptor): Promise<TypedValue | undefined> {
    const vProp = this._propsMap.get(v);
    if (vProp === undefined) {
      throw new GraphPropsMapDiscrepancyError(v);
    }
    vProp.value?.value?.dispose?.();
    vProp.value = undefined;

    const inputVertices = this._adj.getInputs(v);
    let inputs: TypedValueCollection | undefined = new Array(inputVertices.length);
    for (let i = 0; i < inputVertices.length; ++i) {
      const u = inputVertices[i];
      const uProp = this._propsMap.get(u);
      if (uProp === undefined) {
        throw new GraphPropsMapDiscrepancyError(u);
      }
      if (uProp.value) {
        inputs[i] = uProp.value;
      } else {
        inputs = undefined;
        break;
      }
    }

    if (inputs) {
      vProp.value = await vProp.calculator.invokeAsync(inputs);
    }

    return vProp.value;
  }

  public calculateVerticesAsync(dependencies: VerticesDependenciesCollection): Promise<Array<TypedValue | undefined>> {
    const vertexPromisesMap: Map<VertexDescriptor, Promise<TypedValue | undefined>> = new Map();
    const vertexPromises: Array<Promise<TypedValue | undefined>> = [];
    for (const d of dependencies) {
      const promise = new Promise<TypedValue | undefined>((resolve, reject) => {
        const depPromises = d.dependencies?.map((v: VertexDescriptor): Promise<TypedValue | undefined> => {
          const dependency = vertexPromisesMap.get(v);
          if (!dependency) {
            throw new Error('Dependencies expected to be processed earlier');
          }
          return dependency;
        });
        Promise.all(depPromises || []).then(() => {
          this.calculateVertexAsync(d.vertex).then((value) => resolve(value), reject);
        }, reject);
      });
      vertexPromisesMap.set(d.vertex, promise);
      vertexPromises.push(promise);
    }
    return Promise.all(vertexPromises);
  }

  public getValueCalculator(v: VertexDescriptor): IValueCalculator {
    const vProp = this._propsMap.get(v);
    if (vProp === undefined) {
      throw new GraphPropsMapDiscrepancyError(v);
    }
    return vProp.calculator;
  }

  public getValue(v: VertexDescriptor): TypedValue | undefined {
    const vProp = this._propsMap.get(v);
    if (vProp === undefined) {
      throw new GraphPropsMapDiscrepancyError(v);
    }
    return vProp.value;
  }

  public getType(v: VertexDescriptor): Type {
    const vProp = this._propsMap.get(v);
    if (vProp === undefined) {
      throw new GraphPropsMapDiscrepancyError(v);
    }
    return vProp.calculator.resProto;
  }

  public getInputs(): VertexDescriptorCollection {
    const ret: VertexDescriptorCollection = [];
    for (const v of this._adj.getVerticesList()) {
      if (this.getValueCalculator(v).inpProto.length == 0) {
        ret.push(v);
      }
    }
    return ret;
  }

  public getOutputs(): VertexDescriptorCollection {
    const ret: VertexDescriptorCollection = [];
    for (const v of this._adj.getVerticesList()) {
      if (this.getValueCalculator(v).inpProto.length > 0) {
        ret.push(v);
      }
    }
    return ret;
  }

  public getAdjacencyList(): AdjacencyList {
    return this._adj;
  }

  // For Unit Testing
  public __setAdjacencyList(adj: AdjacencyList): void {
    this._adj = adj;
  }
}
